import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import { CopyId } from "@/components/copy-id";
import { VehicleRow, VehicleTypeBadge } from "@/components/vehicle-row";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type User, usersProvider } from "@/providers/users.provider";
import {
  type Vehicle,
  vehiclesProvider,
} from "@/providers/vehicles.provider";
import {
  IconCar,
  IconChevronLeft,
  IconChevronRight,
  IconMotorbike,
  IconPencil,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { formatImageInfo, useImageInfo } from "@/hooks/use-image-info";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 50;


function CreateVehicleSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [vehicleType, setVehicleType] = useState<"car" | "motorcycle">("car");
  const [nickname, setNickname] = useState("");
  const [modList, setModList] = useState("");
  const [horsepower, setHorsepower] = useState("");
  const [drive, setDrive] = useState<"FWD" | "RWD" | "AWD" | "">("");
  const [transmission, setTransmission] = useState<"A" | "M" | "">("");
  const [enginePosition, setEnginePosition] = useState<
    "front" | "center" | "rear" | ""
  >("");
  const [imageFile, setImageFile] = useState<File | null>(null);

  function reset() {
    setMake("");
    setModel("");
    setYear("");
    setVehicleType("car");
    setNickname("");
    setModList("");
    setHorsepower("");
    setDrive("");
    setTransmission("");
    setEnginePosition("");
    setImageFile(null);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }

    setImageFile(file);
    e.target.value = "";
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const parsedYear = parseInt(year, 10);
      const parsedHp = horsepower ? parseInt(horsepower, 10) : undefined;

      const { id } = await vehiclesProvider.create({
        make: make.trim(),
        model: model.trim(),
        year: parsedYear,
        vehicle_type: vehicleType,
        nickname: nickname.trim() || undefined,
        mod_list: modList.trim() || undefined,
        horsepower: parsedHp,
        drive: drive || undefined,
        transmission: transmission || undefined,
        engine_position: enginePosition || undefined,
      });

      const uploadResponse = await vehiclesProvider.getPhotoUploadUrl(id);
      await vehiclesProvider.uploadPhotoToStorage(uploadResponse, imageFile!);
    },
    onSuccess: () => {
      toast.success("Vehicle created successfully");
      reset();
      onCreated();
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const canSubmit =
    make.trim() &&
    model.trim() &&
    year.trim() &&
    !isNaN(parseInt(year, 10)) &&
    imageFile !== null;

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <SheetContent className="sm:max-w-md overflow-y-auto flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle>Create Vehicle</SheetTitle>
          <SheetDescription>
            Add a new vehicle. A photo is required.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 flex flex-col gap-4 py-4">
          {/* Photo upload */}
          <div className="flex flex-col gap-1.5">
            <Label>Photo *</Label>
            <div className="flex items-center gap-3">
              <div
                className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => fileRef.current?.click()}
              >
                {imageFile ? (
                  <img
                    src={URL.createObjectURL(imageFile)}
                    alt="preview"
                    className="size-full object-cover"
                  />
                ) : (
                  <IconPhoto className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="gap-1.5"
                >
                  <IconUpload className="size-3.5" />
                  {imageFile ? "Change photo" : "Choose photo"}
                </Button>
                {imageFile && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-destructive text-left"
                    onClick={() => setImageFile(null)}
                  >
                    Remove
                  </button>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          <Separator />

          {/* Basic info */}
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  placeholder="Toyota"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  placeholder="Supra"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="year">Year *</Label>
                <Input
                  id="year"
                  type="number"
                  placeholder="1993"
                  min={1900}
                  max={new Date().getFullYear() + 1}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Type</Label>
                <Select
                  value={vehicleType}
                  onValueChange={(v) =>
                    setVehicleType(v as "car" | "motorcycle")
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="motorcycle">Motorcycle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nickname">Nickname</Label>
              <Input
                id="nickname"
                placeholder='e.g. "MK4"'
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
              />
            </div>
          </div>

          <Separator />

          {/* Specs */}
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold">Specs</h3>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Drivetrain</Label>
                <Select
                  value={drive}
                  onValueChange={(v) => setDrive(v as typeof drive)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FWD">FWD</SelectItem>
                    <SelectItem value="RWD">RWD</SelectItem>
                    <SelectItem value="AWD">AWD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Transmission</Label>
                <Select
                  value={transmission}
                  onValueChange={(v) =>
                    setTransmission(v as typeof transmission)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Manual</SelectItem>
                    <SelectItem value="A">Automatic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Engine Position</Label>
                <Select
                  value={enginePosition}
                  onValueChange={(v) =>
                    setEnginePosition(v as typeof enginePosition)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="front">Front</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="rear">Rear</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="hp">Horsepower</Label>
                <Input
                  id="hp"
                  type="number"
                  placeholder="320"
                  min={0}
                  value={horsepower}
                  onChange={(e) => setHorsepower(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Mods */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mods">Modifications</Label>
            <Textarea
              id="mods"
              placeholder="Turbo upgrade, Coilovers, Exhaust…"
              value={modList}
              onChange={(e) => setModList(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <SheetFooter className="px-4 pb-4">
          <Button
            className="w-full"
            disabled={!canSubmit || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? "Creating…" : "Create Vehicle"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function VehicleDetailSheet({
  vehicle,
  onClose,
}: {
  vehicle: Vehicle | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [newPhoto, setNewPhoto] = useState<File | null>(null);
  const photoInfo = useImageInfo(newPhoto ? null : vehicle?.photo_url);

  const [isEditing, setIsEditing] = useState(false);
  const [editMake, setEditMake] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editYear, setEditYear] = useState("");
  const [editVehicleType, setEditVehicleType] = useState<"car" | "motorcycle">("car");
  const [editNickname, setEditNickname] = useState("");
  const [editModList, setEditModList] = useState("");
  const [editHorsepower, setEditHorsepower] = useState("");
  const [editDrive, setEditDrive] = useState<"FWD" | "RWD" | "AWD" | "">("");
  const [editTransmission, setEditTransmission] = useState<"A" | "M" | "">("");
  const [editEnginePosition, setEditEnginePosition] = useState<"front" | "center" | "rear" | "">("");

  function populateEditFields(v: Vehicle) {
    setEditMake(v.make);
    setEditModel(v.model);
    setEditYear(String(v.year));
    setEditVehicleType(v.vehicle_type);
    setEditNickname(v.nickname ?? "");
    setEditModList(v.mod_list ?? "");
    setEditHorsepower(v.horsepower != null ? String(v.horsepower) : "");
    setEditDrive(v.drive ?? "");
    setEditTransmission(v.transmission ?? "");
    setEditEnginePosition(v.engine_position ?? "");
  }

  const photoUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const response = await vehiclesProvider.getPhotoUploadUrl(vehicle!.vehicle_id);
      await vehiclesProvider.uploadPhotoToStorage(response, file);
    },
    onSuccess: () => {
      toast.success("Photo updated");
      setNewPhoto(null);
      queryClient.invalidateQueries({ queryKey: ["vehicles-by-user"] });
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (vehicleId: string) => vehiclesProvider.delete(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles-by-user"] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      vehiclesProvider.update(vehicle!.vehicle_id, {
        make: editMake.trim(),
        model: editModel.trim(),
        year: parseInt(editYear, 10),
        vehicle_type: editVehicleType,
        nickname: editNickname.trim() || null,
        mod_list: editModList.trim() || null,
        horsepower: editHorsepower ? parseInt(editHorsepower, 10) : null,
        drive: editDrive || null,
        transmission: editTransmission || null,
        engine_position: editEnginePosition || null,
      }),
    onSuccess: () => {
      toast.success("Vehicle updated");
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ["vehicles-by-user"] });
    },
    onError: (err) => toast.error(err.message),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    setNewPhoto(file);
    e.target.value = "";
  }

  useEffect(() => {
    if (!vehicle) {
      setNewPhoto(null);
      setIsEditing(false);
    } else {
      populateEditFields(vehicle);
    }
  }, [vehicle]);

  const createdAt = vehicle
    ? new Date(vehicle.created_at).toLocaleDateString()
    : "";
  const updatedAt = vehicle
    ? new Date(vehicle.updated_at).toLocaleDateString()
    : "";

  const canSave =
    editMake.trim() &&
    editModel.trim() &&
    editYear.trim() &&
    !isNaN(parseInt(editYear, 10));

  return (
    <Sheet
      open={!!vehicle}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {vehicle && (
          <>
            <SheetHeader>
              <div className="flex items-start justify-between gap-2 pr-8">
                <div className="min-w-0">
                  <SheetTitle>
                    {vehicle.nickname
                      ? `${vehicle.make} ${vehicle.model} "${vehicle.nickname}"`
                      : `${vehicle.make} ${vehicle.model}`}
                  </SheetTitle>
                  <CopyId id={vehicle.vehicle_id} />
                </div>
                {!isEditing && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5"
                    onClick={() => {
                      populateEditFields(vehicle);
                      setIsEditing(true);
                    }}
                  >
                    <IconPencil className="size-3.5" />
                    Edit
                  </Button>
                )}
              </div>
            </SheetHeader>

            {isEditing ? (
              <>
                <div className="px-4 flex flex-col gap-4 py-2">
                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="edit-make">Make *</Label>
                        <Input
                          id="edit-make"
                          placeholder="Toyota"
                          value={editMake}
                          onChange={(e) => setEditMake(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="edit-model">Model *</Label>
                        <Input
                          id="edit-model"
                          placeholder="Supra"
                          value={editModel}
                          onChange={(e) => setEditModel(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="edit-year">Year *</Label>
                        <Input
                          id="edit-year"
                          type="number"
                          placeholder="1993"
                          min={1900}
                          max={new Date().getFullYear() + 1}
                          value={editYear}
                          onChange={(e) => setEditYear(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label>Type</Label>
                        <Select
                          value={editVehicleType}
                          onValueChange={(v) => setEditVehicleType(v as "car" | "motorcycle")}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="car">Car</SelectItem>
                            <SelectItem value="motorcycle">Motorcycle</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="edit-nickname">Nickname</Label>
                      <Input
                        id="edit-nickname"
                        placeholder='e.g. "MK4"'
                        value={editNickname}
                        onChange={(e) => setEditNickname(e.target.value)}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex flex-col gap-3">
                    <h3 className="text-sm font-semibold">Specs</h3>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="flex flex-col gap-1.5">
                        <Label>Drivetrain</Label>
                        <Select
                          value={editDrive}
                          onValueChange={(v) => setEditDrive(v as typeof editDrive)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="FWD">FWD</SelectItem>
                            <SelectItem value="RWD">RWD</SelectItem>
                            <SelectItem value="AWD">AWD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label>Transmission</Label>
                        <Select
                          value={editTransmission}
                          onValueChange={(v) => setEditTransmission(v as typeof editTransmission)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M">Manual</SelectItem>
                            <SelectItem value="A">Automatic</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="flex flex-col gap-1.5">
                        <Label>Engine Position</Label>
                        <Select
                          value={editEnginePosition}
                          onValueChange={(v) => setEditEnginePosition(v as typeof editEnginePosition)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="front">Front</SelectItem>
                            <SelectItem value="center">Center</SelectItem>
                            <SelectItem value="rear">Rear</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="edit-hp">Horsepower</Label>
                        <Input
                          id="edit-hp"
                          type="number"
                          placeholder="320"
                          min={0}
                          value={editHorsepower}
                          onChange={(e) => setEditHorsepower(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-mods">Modifications</Label>
                    <Textarea
                      id="edit-mods"
                      placeholder="Turbo upgrade, Coilovers, Exhaust…"
                      value={editModList}
                      onChange={(e) => setEditModList(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      disabled={!canSave || updateMutation.isPending}
                      onClick={() => updateMutation.mutate()}
                    >
                      {updateMutation.isPending ? "Saving…" : "Save Changes"}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      disabled={updateMutation.isPending}
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="px-4 flex flex-col gap-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <VehicleTypeBadge type={vehicle.vehicle_type} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Owner</span>
                    <span className="font-medium">{vehicle.username}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">User ID</span>
                    <CopyId id={vehicle.user_id} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Make</span>
                    <span>{vehicle.make}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Model</span>
                    <span>{vehicle.model}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Year</span>
                    <span className="tabular-nums">{vehicle.year}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Nickname</span>
                    <span>{vehicle.nickname ?? "—"}</span>
                  </div>
                </div>

                <Separator />

                <div className="px-4 flex flex-col gap-3 text-sm">
                  <h3 className="font-semibold text-sm">Specs</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Drivetrain</span>
                    <span>{vehicle.drive ?? "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Transmission</span>
                    <span>
                      {vehicle.transmission === "A"
                        ? "Automatic"
                        : vehicle.transmission === "M"
                          ? "Manual"
                          : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Engine Position</span>
                    <span className="capitalize">
                      {vehicle.engine_position ?? "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Horsepower</span>
                    <span className="tabular-nums">
                      {vehicle.horsepower != null
                        ? `${vehicle.horsepower} hp`
                        : "—"}
                    </span>
                  </div>
                </div>

                {vehicle.mod_list && (
                  <>
                    <Separator />
                    <div className="px-4 flex flex-col gap-2 text-sm">
                      <h3 className="font-semibold text-sm">Modifications</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {vehicle.mod_list}
                      </p>
                    </div>
                  </>
                )}

                <Separator />

                <div className="px-4 flex flex-col gap-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Created</span>
                    <span>{createdAt}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Last updated</span>
                    <span>{updatedAt}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-muted-foreground">Photo</span>
                    <div
                      className="w-full rounded-lg border bg-muted overflow-hidden flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity max-h-48 min-h-24"
                      onClick={() => fileRef.current?.click()}
                    >
                      {newPhoto ? (
                        <img
                          src={URL.createObjectURL(newPhoto)}
                          alt="New photo preview"
                          className="w-full object-cover max-h-48"
                        />
                      ) : vehicle.photo_url ? (
                        <img
                          src={vehicle.photo_url}
                          alt="Vehicle photo"
                          className="w-full object-cover max-h-48"
                        />
                      ) : (
                        <IconPhoto className="size-8 text-muted-foreground" />
                      )}
                    </div>
                    {photoInfo && (
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {formatImageInfo(photoInfo)}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => fileRef.current?.click()}
                      >
                        <IconUpload className="size-3.5" />
                        {newPhoto ? "Change" : "Choose photo"}
                      </Button>
                      {newPhoto && (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            className="gap-1.5"
                            disabled={photoUploadMutation.isPending}
                            onClick={() => photoUploadMutation.mutate(newPhoto)}
                          >
                            {photoUploadMutation.isPending ? "Uploading…" : "Upload"}
                          </Button>
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-destructive"
                            onClick={() => setNewPhoto(null)}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                </div>

                <Separator />

                <div className="px-4 flex flex-col gap-2">
                  <h3 className="font-semibold text-sm text-destructive">
                    Danger Zone
                  </h3>
                  {deleteMutation.isError && (
                    <p className="text-xs text-destructive">
                      {deleteMutation.error?.message ?? "Failed to delete vehicle."}
                    </p>
                  )}
                  <ConfirmDestructiveDialog
                    confirmValue={vehicle.vehicle_id}
                    confirmLabel="vehicle ID"
                    title="Delete this vehicle?"
                    description="This will permanently delete this vehicle. This cannot be undone."
                    actionLabel="Delete vehicle"
                    onConfirm={() => deleteMutation.mutate(vehicle.vehicle_id)}
                    isPending={deleteMutation.isPending}
                    trigger={({ disabled }) => (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-1.5 self-start"
                        disabled={disabled}
                      >
                        <IconTrash className="size-3.5" />
                        Delete Vehicle
                      </Button>
                    )}
                  />
                </div>
              </>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}


export function VehiclesPage() {
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [debouncedUserQuery, setDebouncedUserQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedUserQuery(userQuery.trim()), 300);
    return () => clearTimeout(id);
  }, [userQuery]);

  const queryClient = useQueryClient();

  const { data: userResults, isFetching: usersFetching } = useQuery({
    queryKey: ["users-search", debouncedUserQuery],
    queryFn: () => usersProvider.search(debouncedUserQuery),
    enabled: debouncedUserQuery.length >= 1,
    staleTime: 30_000,
  });

  const committedUserId = selectedUser?.user_id ?? "";

  const {
    data: vehicles,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["vehicles-by-user", committedUserId, page],
    queryFn: () => vehiclesProvider.getByUser(committedUserId),
    enabled: !!committedUserId,
  });

  const pagedVehicles = vehicles
    ? vehicles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : [];
  const hasMore = pagedVehicles.length === PAGE_SIZE;

  function handleSelectUser(user: User) {
    setSelectedUser(user);
    setUserQuery("");
    setDropdownOpen(false);
    setPage(1);
  }

  function handleClearUser() {
    setSelectedUser(null);
    setUserQuery("");
    setPage(1);
  }

  const showPrompt = !committedUserId;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Vehicles" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex w-full items-center gap-2 sm:w-auto">
            {selectedUser ? (
              <div className="flex w-full items-center gap-2 rounded-lg border px-3 py-1.5 sm:w-80">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {selectedUser.username}
                  </p>
                  <CopyId id={selectedUser.user_id} />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-xs h-6 px-2"
                  onClick={handleClearUser}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="relative w-full sm:w-80">
                <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search users by name, email or username…"
                  value={userQuery}
                  autoComplete="off"
                  onChange={(e) => {
                    setUserQuery(e.target.value);
                    setDropdownOpen(true);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                  className="pl-8 pr-8"
                />
                {userQuery && (
                  <button
                    type="button"
                    onClick={() => setUserQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <IconX className="size-4" />
                  </button>
                )}
                {dropdownOpen && userQuery.trim().length >= 1 && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-md">
                    {usersFetching ? (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        Searching…
                      </p>
                    ) : !userResults?.length ? (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        No users found
                      </p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto">
                        {userResults.map((u) => (
                          <button
                            key={u.user_id}
                            type="button"
                            className="flex w-full flex-col px-3 py-2 text-left hover:bg-accent"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectUser(u)}
                          >
                            <span className="text-sm font-medium">
                              {u.username}
                            </span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {u.user_id}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <div className="flex items-center gap-2 text-muted-foreground">
              <IconCar className="size-4" />
              <span className="text-sm">
                {isLoading
                  ? "Loading…"
                  : vehicles
                    ? `${vehicles.length} vehicle${vehicles.length !== 1 ? "s" : ""}`
                    : ""}
              </span>
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => setCreateOpen(true)}
            >
              <IconPlus className="size-3.5" />
              Create Vehicle
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vehicle ID</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Drive</TableHead>
                <TableHead>Transmission</TableHead>
                <TableHead>Horsepower</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {error?.message ?? "Failed to load vehicles."}
                  </TableCell>
                </TableRow>
              ) : showPrompt ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Search for a user above to load their vehicles.
                  </TableCell>
                </TableRow>
              ) : !pagedVehicles.length ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {page > 1
                      ? "No more vehicles on this page."
                      : "This user has no vehicles."}
                  </TableCell>
                </TableRow>
              ) : (
                pagedVehicles.map((vehicle) => (
                  <VehicleRow
                    key={vehicle.vehicle_id}
                    vehicle={vehicle}
                    onClick={() => setSelectedVehicle(vehicle)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {page > 1 || hasMore ? (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="gap-1"
            >
              <IconChevronLeft className="size-3.5" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              className="gap-1"
            >
              Next
              <IconChevronRight className="size-3.5" />
            </Button>
          </div>
        ) : null}
      </div>

      <VehicleDetailSheet
        vehicle={selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
      />

      <CreateVehicleSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          if (committedUserId) {
            queryClient.invalidateQueries({ queryKey: ["vehicles-by-user"] });
          }
        }}
      />
    </div>
  );
}
